import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useContent } from "../i18n/LanguageContext";
import { useAuth } from "../i18n/AuthContext";
import { toast } from "sonner";
import LanguageSwitcher from "../components/LanguageSwitcher";

const MARBLE = "https://images.unsplash.com/photo-1690049098415-29f96fca22c0?auto=format&fit=crop&w=1400&q=80";

export default function Login() {
  const c = useContent();
  const p = c.portal;
  const { login, user } = useAuth();
  const navigate = useNavigate();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user) navigate("/portal");
  }, [user, navigate]);

  const emailValid = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);

  const submit = async (e) => {
    e.preventDefault();
    if (!emailValid(email)) { toast.error(p.errEmailInvalid); return; }
    if (!password) { toast.error(p.errGeneric); return; }
    setBusy(true);
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success(p.loginOk);
      navigate("/portal");
    } else if (res.error === "invalid_credentials") {
      toast.error(p.loginErr);
    } else {
      toast.error(p.errGeneric);
    }
  };

  const labelCls = "block text-sm font-medium text-[#16233d] mb-2";
  const inputCls =
    "w-full rounded-lg border border-[#e7e2d8] bg-white px-4 py-3.5 text-sm text-[#16233d] placeholder-[#9aa0ad] focus:outline-none focus:border-[#b08d57] focus:ring-2 focus:ring-[#b08d57]/20 transition";

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: image */}
      <div className="hidden lg:block relative order-1">
        <img src={MARBLE} alt="Barlow Consultants" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#16233d]/80 via-[#16233d]/25 to-[#16233d]/10" />
        <div className="absolute top-10 left-12 flex items-center gap-3">
          <div className="w-10 h-10 rounded-md border border-white/40 flex items-center justify-center">
            <span className="font-serif-lux text-xl text-white leading-none">B</span>
          </div>
          <div className="font-serif-lux text-lg text-white font-semibold">Barlow Consultants SL</div>
        </div>
        <div className="absolute bottom-14 left-12 right-12">
          <h2 className="font-serif-lux text-4xl leading-tight text-white">{p.authImageTitle}</h2>
        </div>
      </div>

      {/* Right: form */}
      <div className="flex flex-col min-h-screen order-2">
        <div className="flex items-center justify-between px-6 lg:px-14 h-20 shrink-0">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-md border border-[#e2d3b3] flex items-center justify-center">
              <span className="font-serif-lux text-xl text-[#b08d57] leading-none">B</span>
            </div>
            <div className="leading-tight">
              <div className="font-serif-lux text-lg text-[#16233d] font-semibold">Barlow</div>
              <div className="text-[10px] tracking-[0.18em] text-[#b08d57] uppercase">Consultants SL</div>
            </div>
          </Link>
          <LanguageSwitcher />
        </div>

        <div className="flex-1 flex items-center justify-center px-6 lg:px-14 pb-12">
          <div className="w-full max-w-md">
            <h1 className="font-serif-lux text-4xl text-[#16233d] font-semibold" data-testid="login-title">{p.loginHeadline}</h1>
            <p className="mt-2 text-[#5c6577] text-base">{p.loginSub}</p>

            <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
              <div>
                <label className={labelCls}>{p.emailLabel}</label>
                <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} data-testid="login-email-input" />
              </div>
              <div>
                <label className={labelCls}>{p.passwordLabel}</label>
                <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} data-testid="login-password-input" />
                <div className="mt-2 text-right">
                  <Link to="/forgot-password" className="text-sm font-medium text-[#b08d57] hover:text-[#8f6f3f] transition-colors" data-testid="login-forgot-link">{p.forgotPw}</Link>
                </div>
              </div>
              <button type="submit" disabled={busy} className="btn-gold rounded-lg w-full py-4 font-medium disabled:opacity-60" data-testid="login-submit-button">
                {p.loginBtn}
              </button>
            </form>

            <div className="mt-6 text-sm text-[#5c6577]">
              {p.noAccount}{" "}
              <Link to="/register" className="font-medium text-[#b08d57] hover:text-[#8f6f3f] transition-colors" data-testid="login-to-register">{p.toRegister}</Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
